import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Search, Loader2, X, FileText, MapPin, Presentation, Users2, GraduationCap, Building2, User, Layers, FolderKanban, Boxes, ListChecks, BookOpen, Receipt, Calendar, CornerDownLeft, Clock, History } from 'lucide-react';
import { searchAPI } from '../../services/api';

const RECORDS_KEY = 'skilltrac_recent_records';
const SEARCHES_KEY = 'skilltrac_recent_searches';
const readLS = (k) => { try { return JSON.parse(localStorage.getItem(k)) || []; } catch { return []; } };
const writeLS = (k, v) => { try { localStorage.setItem(k, JSON.stringify(v)); } catch { /* ignore */ } };

const TYPE_ICON = {
  'Private Deliveries': FileText,
  'Partner Conferences': Users2,
  'Regional In-Person': MapPin,
  '1:Many PPRW': Presentation,
  'Sessions': Calendar,
  'Courses': GraduationCap,
  'Partners': Building2,
  'Trainers': User,
  'Content Items': Layers,
  'Content Projects': FolderKanban,
  'Platform Projects': Boxes,
  'Platform Tasks': ListChecks,
  'KB Articles': BookOpen,
  'Purchase Orders': Receipt,
};

export const GlobalSearch = () => {
  const navigate = useNavigate();
  const [q, setQ] = useState('');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const [recentRecords, setRecentRecords] = useState(() => readLS(RECORDS_KEY));
  const [recentSearches, setRecentSearches] = useState(() => readLS(SEARCHES_KEY));
  const wrapRef = useRef(null);
  const inputRef = useRef(null);

  const runSearch = useCallback(async (term) => {
    if (!term || term.trim().length < 2) { setData(null); setLoading(false); return; }
    setLoading(true);
    try {
      const res = await searchAPI.query(term.trim());
      setData(res);
    } catch { setData({ total: 0, groups: [] }); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => {
    const t = setTimeout(() => runSearch(q), 300);
    return () => clearTimeout(t);
  }, [q, runSearch]);

  useEffect(() => {
    const onDocClick = (e) => { if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false); };
    const onKey = (e) => {
      if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === 'k') { e.preventDefault(); inputRef.current?.focus(); setOpen(true); }
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', onDocClick);
    document.addEventListener('keydown', onKey);
    return () => { document.removeEventListener('mousedown', onDocClick); document.removeEventListener('keydown', onKey); };
  }, []);

  const goto = (record) => {
    // remember opened record + the search term that led to it
    const rec = { title: record.title, subtitle: record.subtitle, type: record.type, url: record.url };
    const nextRecords = [rec, ...recentRecords.filter(r => r.url !== rec.url)].slice(0, 6);
    setRecentRecords(nextRecords); writeLS(RECORDS_KEY, nextRecords);
    const term = q.trim();
    if (term.length >= 2) {
      const nextSearches = [term, ...recentSearches.filter(s => s.toLowerCase() !== term.toLowerCase())].slice(0, 6);
      setRecentSearches(nextSearches); writeLS(SEARCHES_KEY, nextSearches);
    }
    setOpen(false);
    setQ('');
    setData(null);
    navigate(record.url);
  };

  const clearRecents = () => {
    setRecentRecords([]); setRecentSearches([]);
    writeLS(RECORDS_KEY, []); writeLS(SEARCHES_KEY, []);
  };

  const showResults = open && q.trim().length >= 2;
  const showRecents = open && q.trim().length < 2 && (recentRecords.length > 0 || recentSearches.length > 0);

  return (
    <div ref={wrapRef} className="relative w-full max-w-xl" data-testid="global-search">
      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground pointer-events-none" />
        <input
          ref={inputRef}
          type="text"
          value={q}
          onChange={(e) => { setQ(e.target.value); setOpen(true); }}
          onFocus={() => setOpen(true)}
          placeholder="Search courses, deliveries, content, partners…"
          className="w-full h-10 pl-10 pr-16 rounded-lg border border-border bg-white text-sm focus:outline-none focus:ring-2 focus:ring-lime/40 focus:border-lime transition-shadow"
          data-testid="global-search-input"
        />
        {q ? (
          <button onClick={() => { setQ(''); setData(null); inputRef.current?.focus(); }} className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground" data-testid="global-search-clear">
            <X className="w-4 h-4" />
          </button>
        ) : (
          <kbd className="absolute right-3 top-1/2 -translate-y-1/2 hidden sm:flex items-center gap-0.5 text-[10px] text-muted-foreground border border-border rounded px-1.5 py-0.5">⌘K</kbd>
        )}
      </div>

      {showResults && (
        <div className="absolute z-50 mt-2 w-full bg-white border border-border rounded-xl shadow-xl max-h-[70vh] overflow-y-auto" data-testid="global-search-results">
          {loading ? (
            <div className="flex items-center gap-2 justify-center py-8 text-sm text-muted-foreground">
              <Loader2 className="w-4 h-4 animate-spin" />Searching…
            </div>
          ) : !data || data.total === 0 ? (
            <div className="py-8 text-center text-sm text-muted-foreground" data-testid="global-search-empty">
              No matches for “{q}”.
            </div>
          ) : (
            <div className="py-2">
              <p className="px-4 py-1 text-[11px] uppercase tracking-wide text-muted-foreground">{data.total} result{data.total !== 1 ? 's' : ''}</p>
              {data.groups.map((g) => {
                const Icon = TYPE_ICON[g.type] || FileText;
                return (
                  <div key={g.type} className="py-1" data-testid={`search-group-${g.type}`}>
                    <p className="px-4 py-1 text-xs font-semibold text-foreground/70 flex items-center gap-1.5">
                      <Icon className="w-3.5 h-3.5 text-lime-600" />{g.type}
                    </p>
                    {g.results.map((r) => (
                      <button
                        key={`${g.type}-${r.id}`}
                        onClick={() => goto({ ...r, type: g.type })}
                        className="w-full text-left px-4 py-2 hover:bg-lime/5 flex items-center justify-between gap-3 group"
                        data-testid={`search-result-${r.id}`}
                      >
                        <div className="min-w-0">
                          <p className="text-sm font-medium truncate">{r.title}</p>
                          {r.subtitle && <p className="text-xs text-muted-foreground truncate">{r.subtitle}</p>}
                        </div>
                        <CornerDownLeft className="w-3.5 h-3.5 text-muted-foreground opacity-0 group-hover:opacity-100 flex-shrink-0" />
                      </button>
                    ))}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}

      {showRecents && (
        <div className="absolute z-50 mt-2 w-full bg-white border border-border rounded-xl shadow-xl max-h-[70vh] overflow-y-auto" data-testid="global-search-recents">
          <div className="flex items-center justify-between px-4 pt-3 pb-1">
            <p className="text-[11px] uppercase tracking-wide text-muted-foreground">Recent</p>
            <button onClick={clearRecents} className="text-[11px] text-muted-foreground hover:text-foreground" data-testid="global-search-clear-recents">Clear</button>
          </div>
          {recentRecords.length > 0 && (
            <div className="py-1">
              <p className="px-4 py-1 text-xs font-semibold text-foreground/70 flex items-center gap-1.5"><Clock className="w-3.5 h-3.5 text-lime-600" />Recently opened</p>
              {recentRecords.map((r) => {
                const Icon = TYPE_ICON[r.type] || FileText;
                return (
                  <button key={r.url} onClick={() => goto(r)} className="w-full text-left px-4 py-2 hover:bg-lime/5 flex items-center gap-2.5" data-testid={`recent-record-${r.url}`}>
                    <Icon className="w-3.5 h-3.5 text-muted-foreground flex-shrink-0" />
                    <div className="min-w-0">
                      <p className="text-sm font-medium truncate">{r.title}</p>
                      {r.subtitle && <p className="text-xs text-muted-foreground truncate">{r.type} · {r.subtitle}</p>}
                    </div>
                  </button>
                );
              })}
            </div>
          )}
          {recentSearches.length > 0 && (
            <div className="py-1 border-t border-border">
              <p className="px-4 py-1 text-xs font-semibold text-foreground/70 flex items-center gap-1.5"><History className="w-3.5 h-3.5 text-lime-600" />Recent searches</p>
              {recentSearches.map((s) => (
                <button key={s} onClick={() => { setQ(s); inputRef.current?.focus(); }} className="w-full text-left px-4 py-2 hover:bg-lime/5 flex items-center gap-2.5 text-sm" data-testid={`recent-search-${s}`}>
                  <Search className="w-3.5 h-3.5 text-muted-foreground flex-shrink-0" />
                  <span className="truncate">{s}</span>
                </button>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default GlobalSearch;
